'use clinet';

import { Content, KeyTextField } from '@prismicio/client';
import { FC, memo, RefObject, useState } from 'react';

import { MinusIcon, PlusIcon } from '@/assets';
import { Simplify } from '../../../../../prismicio-types';

import { screenSizes } from '@/constants';
import { ScreenSize, useOutsideClick } from '@/hooks';
import { ProspectData } from '@/types/salesforce';

import styles from './styles.module.scss';

const engineLandingValues = (index: number, data?: ProspectData) => {
  if (!data) return null;
  if (index === 0) {
    return `$${data.LoanAmount__c.toLocaleString('en-US')}`;
  }
  if (index === 1) {
    if (data.LoanPurpose__c === 'debt_consolidation') {
      return 'Consolidate';
    }
    if (data.LoanPurpose__c === 'credit_card_refi') {
      return 'Refinance';
    }
    return 'Personal loans';
  }
  return `$${data.AnnualIncomeBeforeTaxes__c.toLocaleString('en-US')}`;
};

const debtValues = [
  '$0 - $9,999',
  '$10,000 - $19,999',
  '$20,000 - $29,999',
  '$30,000 - $39,999',
  '$40,000 - $49,999',
  '$50,000 - $59,999',
  '$60,000 - $69,999',
  '$70,000 - $79,999',
  '$80,000 - $89,999',
  '$90,000 - $99,999',
  '$100,000+',
];

const engineLandingDescription = (index: number, data?: ProspectData) => {
  if (!data) return null;
  if (index === 0) {
    const loanRanges = [
      { max: 9999, range: debtValues[0], num: 358 },
      { max: 19999, range: debtValues[1], num: 243 },
      { max: 29999, range: debtValues[2], num: 191 },
      { max: 39999, range: debtValues[3], num: 166 },
      { max: 49999, range: debtValues[4], num: 89 },
      { max: 59999, range: debtValues[5], num: 82 },
      { max: 69999, range: debtValues[6], num: 17 },
      { max: 79999, range: debtValues[7], num: 17 },
      { max: 89999, range: debtValues[8], num: 80 },
      { max: 99999, range: debtValues[9], num: 80 },
      { max: Infinity, range: debtValues[10], num: 80 },
    ];

    const amount = Number(data.LoanAmount__c.toString().replace(/\D/g, ''));

    const match = loanRanges.find((r) => amount <= r.max);

    if (match) {
      return `We've made over $${match.num} million in offers for people looking to borrow between ${match.range}`;
    }
  }
  if (index === 1) {
    let num;
    if (data.LoanPurpose__c === 'debt_consolidation') {
      num = 528;
    } else {
      num = 229;
    }
    return `Over $${num} million in loans were funded this year for ${data.LoanPurpose__c.toLowerCase().replace(/_/g, ' ')}`;
  }

  const income = Number(
    data.AnnualIncomeBeforeTaxes__c.toString().replace(/\D/g, '')
  );
  let num;
  if (income <= 35999) {
    num = 69;
  }
  if (income > 35999 && income <= 71999) {
    num = 539;
  }
  if (income > 71999 && income <= 107999) {
    num = 502;
  }
  if (income > 107999 && income <= 143999) {
    num = 291;
  }
  if (income > 143999) {
    num = 488;
  }
  return `Over $${num} million in loans have been funded for people in your income range`;
};

const generateUpToText = (
  upToText: KeyTextField,
  index: number,
  variation:
    | 'default'
    | 'withoutSubtitle'
    | 'lightTheme'
    | 'withoutSubtitleLink'
    | 'engineLanding',
  data?: ProspectData
) => {
  if (variation !== 'engineLanding') return upToText;
  if (!data) return upToText;
  if (index === 1) {
    if (
      data.LoanPurpose__c !== 'debt_consolidation' &&
      data.LoanPurpose__c !== 'credit_card_refi'
    ) {
      return `${upToText} for`;
    }
    return `${upToText} to`;
  }
  return upToText;
};

type Props = {
  card: Simplify<Content.BenefitsSliceDefaultPrimaryCardsItem>;
  index: number;
  isVisible: boolean;
  screenSize: ScreenSize;
  cardId: string;
  variation:
    | 'default'
    | 'withoutSubtitle'
    | 'lightTheme'
    | 'withoutSubtitleLink'
    | 'engineLanding';
  prospect?: ProspectData;
};

const CardComponent: FC<Props> = ({
  card,
  index,
  isVisible,
  screenSize,
  cardId,
  variation,
  prospect,
}) => {
  const [isOpenDescr, setIsOpenDescr] = useState(false);

  const cardRef = useOutsideClick(() => {
    if (screenSize.width >= screenSizes.desktop) return;

    if (isOpenDescr) setIsOpenDescr(false);
  });

  const handleToggleDescription = () => {
    if (screenSize.width >= screenSizes.desktop) return;

    setIsOpenDescr((prev) => !prev);
  };

  return (
    <li
      id={cardId}
      ref={cardRef as RefObject<HTMLLIElement>}
      className={`${styles.card} ${isVisible ? styles.isVisible : ''}`}
      key={card.card_title}
      style={{ transitionDelay: `${index * 0.13}s` }}
    >
      <div className={`${styles.cardTop} `}>
        <p className={styles.title}>{card.card_title}</p>
        <button
          onClick={handleToggleDescription}
          className={styles.button}
          type="button"
          aria-label={`Show info ${card.card_title}`}
        >
          <PlusIcon />
        </button>
      </div>
      <div>
        <p className={styles.upToText}>
          {generateUpToText(card.value_heading, index, variation, prospect)}
        </p>
        <p
          className={`${styles.upTo}  ${variation === 'engineLanding' && styles.smallUpTo}`}
        >
          {variation === 'engineLanding'
            ? engineLandingValues(index, prospect)
            : card.value}
        </p>
      </div>
      <div
        className={`${styles.hiddenInfo} ${isOpenDescr ? styles.hiddenInfoActive : ''}`}
      >
        <div className={`${styles.cardTop} ${styles.cardTopHidden}`}>
          <p className={styles.title}>{card.card_title}</p>
          <button
            onClick={handleToggleDescription}
            className={styles.button}
            type="button"
            aria-label={`Hide info ${card.card_title}`}
          >
            <MinusIcon />
          </button>
        </div>
        <p className={styles.description}>
          {variation === 'engineLanding'
            ? engineLandingDescription(index, prospect)
            : card.description}
        </p>
      </div>
    </li>
  );
};

export const Card = memo(CardComponent);
